import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, media } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "./AdminLayout";
import { ListingDialog } from "./ListingDialog";

const HotelRow = ({ h, onEdit, onDelete }) => (
  <tr className="border-t border-stone-100 hover:bg-stone-50/60" data-testid={`listing-row-${h.id}`}>
    <td className="px-5 py-3">
      <div className="flex items-center gap-4">
        <div className="w-14 h-10 rounded bg-stone-100 overflow-hidden shrink-0">{h.image && <img src={media(h.image)} alt="" className="w-full h-full object-cover" />}</div>
        <span className="text-sm">{h.name}</span>
      </div>
    </td>
    <td className="px-5 py-3 text-sm text-stone-600">{h.city}</td>
    <td className="px-5 py-3">
      <div className="flex justify-end gap-1">
        <a href={`/hotel/${h.slug}`} target="_blank" rel="noreferrer" className="p-2 text-stone-500 hover:text-charcoal" data-testid={`listing-view-${h.id}`} aria-label="View"><ExternalLink className="w-4 h-4" /></a>
        <button onClick={() => onEdit(h)} className="p-2 text-stone-500 hover:text-charcoal" data-testid={`listing-edit-${h.id}`} aria-label="Edit"><Pencil className="w-4 h-4" /></button>
        <button onClick={() => onDelete(h)} className="p-2 text-red-500 hover:text-red-700" data-testid={`listing-delete-${h.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
      </div>
    </td>
  </tr>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { reload } = useSite();
  const [hotels, setHotels] = useState([]);
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [dialog, setDialog] = useState({ open: false, hotel: null });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const [h, c] = await Promise.all([api.get("/admin/hotels"), api.get("/cities")]);
    setHotels(h.data);
    setCities(c.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => hotels.filter((h) => (!city || h.city_id === city) && h.name.toLowerCase().includes(q.toLowerCase())), [hotels, q, city]);

  const del = async () => {
    try {
      await api.delete(`/admin/hotels/${toDelete.id}`);
      toast.success("Listing deleted");
      setToDelete(null);
      load();
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Hotel Listings</h1>
          <p className="text-sm text-stone-500 mt-1" data-testid="listing-total">{hotels.length} total · signed in as {user.email}</p>
        </div>
        <button onClick={() => setDialog({ open: true, hotel: null })} className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white rounded-md px-5 py-2.5 text-sm font-bold shadow-sm" data-testid="new-listing-btn"><Plus className="w-4 h-4" />New Listing</button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hotels…" className="w-full border border-stone-300 bg-white rounded-md pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-crimson/30" data-testid="listing-search-input" />
        </div>
        <select value={city} onChange={(e) => setCity(e.target.value)} className="sm:w-56 border border-stone-300 bg-white rounded-md px-3 py-2.5 text-sm" data-testid="listing-city-filter">
          <option value="">All Cities</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[560px]" data-testid="listings-table">
          <thead className="bg-stone-50 text-left text-sm text-stone-500">
            <tr><th className="px-5 py-3 font-normal">Hotel</th><th className="px-5 py-3 font-normal">City</th><th className="px-5 py-3 font-normal text-right">Actions</th></tr>
          </thead>
          <tbody>
            {shown.map((h) => <HotelRow key={h.id} h={h} onEdit={(x) => setDialog({ open: true, hotel: x })} onDelete={setToDelete} />)}
            {!shown.length && <tr><td colSpan={3} className="text-center text-sm text-stone-400 py-10" data-testid="listings-empty">No listings found.</td></tr>}
          </tbody>
        </table>
      </div>
      <ListingDialog
        open={dialog.open}
        hotel={dialog.hotel}
        cities={cities}
        onClose={() => setDialog({ open: false, hotel: null })}
        onSaved={() => { setDialog({ open: false, hotel: null }); load(); reload(); }}
        onCityAdded={(c) => setCities((cs) => [...cs, c].sort((a, b) => a.name.localeCompare(b.name)))}
      />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="admin-root" data-testid="delete-listing-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete listing?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" will be removed from the directory.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-listing-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-crimson hover:bg-crimson-hover" data-testid="delete-listing-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
